/**
 * The in-app notification for a new patient WhatsApp message, on every PM screen.
 *
 * Notifications (#293) sends `WhatsAppInboxChanged { conversationId, reason, at }` on the existing
 * NotificationsHub, to the org's inbox staff only. The event carries ids only; this hook:
 *   1. refreshes the inbox list and the rail badge (any reason);
 *   2. for a new patient message (`reason: "inbound"`), notifies ONCE per browser (see ./inbox-notify-tabs.ts):
 *      - visible tab: a toast "New WhatsApp · <name or masked number>" with a 40-character preview and Open;
 *      - hidden tab: an OS notification saying only "New WhatsApp message", and only when the user already
 *        allowed notifications (this never asks).
 *   No notification while muted (./inbox-notify-mute.ts) or while this tab has that thread open.
 *
 * Gate: `inboxNotifyEnabled()` (VITE_INBOX_NOTIFY, default ON, and the inbox flag). The inbox page's 5 s poll
 * stays as the fallback when the hub is down.
 */
import { useEffect, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuthStore } from '@/auth/auth-store';
import { NOTIFICATIONS_SIGNALR_HUB } from '@/notifications/lib/notification-constants';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import type { Conversation } from '../inbox-types';
import { readChangedConversation } from '../services/inbox-changed-conversation';
import { inboxUnreadTotalKey } from '../services/use-inbox-unread-total';
import { inboxKeys } from '../services/use-whatsapp-inbox';
import { isInboxNotifyMuted } from './inbox-notify-mute';
import {
  INBOX_CHANGED_EVENT,
  OS_NOTIFICATION_BODY,
  REASON_INBOUND,
  readInboxChanged,
  toastPreview,
  toastTitle,
  type InboxChangedEvent,
} from './inbox-notify-text';
import { HIDDEN_TAB_DELAY_MS, createTabClaims, type TabClaims } from './inbox-notify-tabs';
import { openInboxThread } from './inbox-open-thread';

export interface NotifyDeps {
  claims: TabClaims;
  isHidden: () => boolean;
  isMuted: () => boolean;
  openThread: () => string | null;
  osAllowed: () => boolean;
  wait: (ms: number) => Promise<void>;
  readConversation: (conversationId: string, at: string) => Promise<Conversation | null>;
  showToast: (title: string, preview: string, open: () => void) => void;
  showOsNotification: (open: () => void) => void;
  open: (conversationId: string) => void;
}

/** One inbound event → at most one notification in this browser. Exported for the tests. */
export async function notifyInbound(event: InboxChangedEvent, d: NotifyDeps): Promise<void> {
  if (d.isMuted()) return;
  const hidden = d.isHidden();
  if (hidden) await d.wait(HIDDEN_TAB_DELAY_MS);
  if (!d.claims.claim(`${event.conversationId}@${event.at}`)) return;
  // Already reading it. Claimed first, so the hidden tabs stay quiet too.
  if (!hidden && d.openThread() === event.conversationId) return;
  const open = () => d.open(event.conversationId);
  if (hidden) {
    if (d.osAllowed()) d.showOsNotification(open);
    return;
  }
  const conversation = await d.readConversation(event.conversationId, event.at);
  d.showToast(
    conversation ? toastTitle(conversation) : 'New WhatsApp message',
    conversation ? toastPreview(conversation.lastMessagePreview) : '',
    open,
  );
}

function browserDeps(qc: QueryClient, claims: TabClaims, open: (conversationId: string) => void): NotifyDeps {
  return {
    claims,
    isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
    isMuted: isInboxNotifyMuted,
    openThread: openInboxThread,
    osAllowed: () => typeof Notification !== 'undefined' && Notification.permission === 'granted',
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    readConversation: (id, at) => readChangedConversation(qc, id, at),
    showToast: (title, preview, onOpen) =>
      toast(title, {
        description: preview || undefined,
        action: { label: 'Open', onClick: onOpen },
        duration: 8_000,
      }),
    showOsNotification: (onOpen) => {
      try {
        const n = new Notification('Scrips', { body: OS_NOTIFICATION_BODY, tag: 'scrips-inbox' });
        n.onclick = () => {
          window.focus();
          onOpen();
          n.close();
        };
      } catch {
        // Some browsers only allow notifications from a service worker; the badge still counts.
      }
    },
    open,
  };
}

export function useInboxNewMessageNotify({ enabled, onOpen }: { enabled: boolean; onOpen: (conversationId: string) => void }): void {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();
  // The caller's navigate changes with the route; the hub must not reconnect on every navigation.
  const onOpenRef = useRef(onOpen);
  useEffect(() => {
    onOpenRef.current = onOpen;
  }, [onOpen]);

  // Synchronises with the Notifications SignalR hub (external system).
  useEffect(() => {
    if (!enabled || !userId) return;

    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(NOTIFICATIONS_SIGNALR_HUB, getToken);
    const claims = createTabClaims();
    const deps = browserDeps(qc, claims, (id) => onOpenRef.current(id));

    hub.on(INBOX_CHANGED_EVENT, (...args: unknown[]) => {
      const event = readInboxChanged(args);
      if (!event) return;
      void qc.invalidateQueries({ queryKey: inboxKeys.lists() });
      void qc.invalidateQueries({ queryKey: inboxUnreadTotalKey });
      if (event.reason !== REASON_INBOUND) return;
      void notifyInbound(event, deps);
    });

    let disposed = false;
    const startSafely = async () => {
      try {
        if (hub.state === HubConnectionState.Disconnected) await hub.start();
        if (disposed && hub.state !== HubConnectionState.Disconnected) await hub.stop();
      } catch {
        // Auto-reconnect handles real failures; StrictMode aborts are expected.
      }
    };
    void startSafely();

    return () => {
      disposed = true;
      claims.close();
      if (hub.state === HubConnectionState.Connected) void hub.stop().catch(() => {});
    };
  }, [enabled, userId, qc]);
}
